import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { studyNotesService, slidesService, generateSlug } from '../../services/studyNotesService';
import { bibleService } from '../../services/bibleService';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { trackStudyNoteCreated } from '../../utils/ga';
import { QRCodeSVG } from 'qrcode.react';

const BIBLE_VERSIONS = ['KJV', 'NKJV', 'NIV', 'ESV', 'NLT', 'NASB', 'CSB', 'AMP', 'MSG'];
const THEMES = [
  { value: 'light', label: 'Light', icon: 'Sun' },
  { value: 'dark', label: 'Dark', icon: 'Moon' },
  { value: 'high-contrast', label: 'High Contrast', icon: 'Contrast' },
];
const FONT_SIZES = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
];

const emptySlide = (type = 'text') => ({
  _id: Math.random()?.toString(36)?.slice(2),
  slideType: type,
  verseReference: '',
  textContent: '',
  versionOverride: '',
  slideTimerSeconds: null,
});

// Parse a verse reference like "John 3:16" or "Romans 8:1-5"
const parseVerseRef = (ref) => {
  if (!ref) return null;
  const match = ref?.trim()?.match(/^(.+?)\s+(\d+):(\d+)(?:-(\d+))?$/);
  if (!match) return null;
  return {
    book: match?.[1],
    chapter: parseInt(match?.[2]),
    verseStart: parseInt(match?.[3]),
    verseEnd: match?.[4] ? parseInt(match?.[4]) : parseInt(match?.[3]),
  };
};

const SlideCard = ({ slide, index, total, onChange, onDelete, onMoveUp, onMoveDown, defaultVersion }) => {
  const charCount = slide?.textContent?.length || 0;
  const isOverLimit = charCount > 255;

  // Verse validation state
  const [verseValidating, setVerseValidating] = useState(false);
  const [verseValid, setVerseValid] = useState(null); // null = unchecked, true = valid, false = invalid
  const [verseError, setVerseError] = useState('');
  const verseValidateTimeout = useRef(null);

  const validateVerse = async (ref, version) => {
    if (!ref?.trim()) {
      setVerseValid(null);
      setVerseError('');
      return;
    }
    const parsed = parseVerseRef(ref);
    if (!parsed) {
      setVerseValid(false);
      setVerseError('Format should be: Book Chapter:Verse (e.g. John 3:16 or Romans 8:1-5)');
      return;
    }
    setVerseValidating(true);
    setVerseError('');
    try {
      const ver = version || defaultVersion || 'KJV';
      const { data } = await bibleService?.getChapterVerses(parsed?.book, parsed?.chapter, ver);
      if (!data || data?.length === 0) {
        setVerseValid(false);
        setVerseError(`"${ref}" not found in ${ver}. Check the reference or try a different version.`);
      } else {
        const found = data?.filter(v => v?.verse_number >= parsed?.verseStart && v?.verse_number <= parsed?.verseEnd);
        if (found?.length === 0) {
          setVerseValid(false);
          setVerseError(`Verse ${parsed?.verseStart}${parsed?.verseEnd !== parsed?.verseStart ? '-' + parsed?.verseEnd : ''} not found in ${parsed?.book} ${parsed?.chapter}.`);
        } else {
          setVerseValid(true);
          setVerseError('');
        }
      }
    } catch {
      setVerseValid(false);
      setVerseError('Could not validate verse. Check your connection.');
    } finally {
      setVerseValidating(false);
    }
  };

  const handleVerseRefChange = (value) => {
    onChange(index, 'verseReference', value);
    setVerseValid(null);
    clearTimeout(verseValidateTimeout?.current);
    verseValidateTimeout.current = setTimeout(() => {
      validateVerse(value, slide?.versionOverride);
    }, 800);
  };

  const handleVersionChange = (value) => {
    onChange(index, 'versionOverride', value);
    if (slide?.verseReference) {
      clearTimeout(verseValidateTimeout?.current);
      verseValidateTimeout.current = setTimeout(() => {
        validateVerse(slide?.verseReference, value);
      }, 400);
    }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-4 group relative">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
            slide?.slideType === 'title' ? 'bg-primary text-primary-foreground' :
            slide?.slideType === 'verse' ? 'bg-accent/20 text-accent-foreground' :
            'bg-muted text-muted-foreground'
          }`}>
            {index + 1}
          </div>
          <span className="text-xs font-medium text-muted-foreground capitalize">
            {slide?.slideType === 'title' ? '📌 Title Slide' : slide?.slideType === 'verse' ? '📖 Verse' : '📝 Text'}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => onMoveUp(index)} disabled={index === 0} className="p-1 hover:bg-muted rounded disabled:opacity-30 transition-colors">
            <Icon name="ChevronUp" size={14} />
          </button>
          <button onClick={() => onMoveDown(index)} disabled={index === total - 1} className="p-1 hover:bg-muted rounded disabled:opacity-30 transition-colors">
            <Icon name="ChevronDown" size={14} />
          </button>
          <button onClick={() => onDelete(index)} className="p-1 hover:bg-destructive/10 rounded transition-colors">
            <Icon name="Trash2" size={14} color="var(--color-destructive)" />
          </button>
        </div>
      </div>
      {slide?.slideType === 'verse' && (
        <div className="space-y-2">
          <div className="relative">
            <input
              type="text"
              placeholder="Verse reference (e.g. John 3:16 or Romans 8:1-5)"
              value={slide?.verseReference || ''}
              onChange={e => handleVerseRefChange(e?.target?.value)}
              className={`w-full px-3 py-2 bg-background border rounded-lg text-sm focus:outline-none focus:ring-2 pr-8 ${
                verseValid === false ? 'border-destructive focus:ring-destructive/30' :
                verseValid === true ? 'border-green-500 focus:ring-green-500/30': 'border-border focus:ring-primary/30'
              }`}
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2">
              {verseValidating ? (
                <div className="w-3 h-3 border border-muted-foreground border-t-transparent rounded-full animate-spin" />
              ) : verseValid === true ? (
                <Icon name="CheckCircle" size={14} color="#22c55e" />
              ) : verseValid === false ? (
                <Icon name="AlertCircle" size={14} color="var(--color-destructive)" />
              ) : null}
            </div>
          </div>
          {verseError && (
            <p className="text-xs text-destructive flex items-center gap-1">
              <Icon name="AlertCircle" size={11} />
              {verseError}
            </p>
          )}
          {verseValid === true && (
            <p className="text-xs text-green-600 flex items-center gap-1">
              <Icon name="CheckCircle" size={11} />
              Verse found
            </p>
          )}
          <div className="flex gap-2">
            <select
              value={slide?.versionOverride || ''}
              onChange={e => handleVersionChange(e?.target?.value)}
              className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              <option value="">Default version</option>
              {BIBLE_VERSIONS?.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
            <input
              type="number"
              placeholder="Timer (sec)"
              value={slide?.slideTimerSeconds || ''}
              onChange={e => onChange(index, 'slideTimerSeconds', e?.target?.value ? parseInt(e?.target?.value) : null)}
              min="5"
              max="600"
              className="w-28 px-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
        </div>
      )}
      {(slide?.slideType === 'text' || slide?.slideType === 'title') && (
        <div className="space-y-2">
          <div className="relative">
            <textarea
              placeholder={slide?.slideType === 'title' ? 'Title slide content (sermon title, speaker, date...)' : 'Text content (max 255 characters)'}
              value={slide?.textContent || ''}
              onChange={e => onChange(index, 'textContent', e?.target?.value)}
              rows={3}
              maxLength={255}
              className={`w-full px-3 py-2 bg-background border rounded-lg text-sm focus:outline-none focus:ring-2 resize-none ${
                isOverLimit ? 'border-destructive focus:ring-destructive/30' : 'border-border focus:ring-primary/30'
              }`}
            />
            <span className={`absolute bottom-2 right-2 text-xs ${isOverLimit ? 'text-destructive' : 'text-muted-foreground'}`}>
              {charCount}/255
            </span>
          </div>
          <input
            type="number"
            placeholder="Slide timer (seconds, optional)"
            value={slide?.slideTimerSeconds || ''}
            onChange={e => onChange(index, 'slideTimerSeconds', e?.target?.value ? parseInt(e?.target?.value) : null)}
            min="5"
            max="600"
            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
      )}
    </div>
  );
};

const StudyNoteEditor = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isNew = !id;

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [error, setError] = useState(null);
  const [slugChecking, setSlugChecking] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState(true);
  const [showQR, setShowQR] = useState(false);
  const [noteId, setNoteId] = useState(id || null);

  const [form, setForm] = useState({
    title: '',
    speaker: '',
    description: '',
    churchName: '',
    defaultVersion: 'KJV',
    syncMode: 'self-paced',
    theme: 'light',
    fontSize: 'medium',
    slug: generateSlug(6),
  });

  const [slides, setSlides] = useState([
    { ...emptySlide('title'), textContent: '' },
  ]);

  const slugTimeout = useRef(null);

  useEffect(() => {
    if (!isNew && id) loadNote();
  }, [id]);

  const loadNote = async () => {
    try {
      setLoading(true);
      const note = await studyNotesService?.getNoteById(id);
      if (!note) { navigate('/tools/notes'); return; }
      setForm({
        title: note?.title || '',
        speaker: note?.speaker || '',
        description: note?.description || '',
        churchName: note?.church_name || '',
        defaultVersion: note?.default_version || 'KJV',
        syncMode: note?.sync_mode || 'self-paced',
        theme: note?.theme || 'light',
        fontSize: note?.font_size || 'medium',
        slug: note?.slug || '',
      });
      const slideData = await slidesService?.getSlides(id);
      setSlides(slideData?.length > 0 ? slideData?.map(s => ({ ...s, _id: s?.id || Math.random()?.toString(36)?.slice(2) })) : [{ ...emptySlide('title') }]);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (field === 'slug') {
      clearTimeout(slugTimeout?.current);
      slugTimeout.current = setTimeout(() => checkSlug(value), 500);
    }
  };

  const checkSlug = async (slug) => {
    if (!slug) return;
    setSlugChecking(true);
    try {
      const available = await studyNotesService?.isSlugAvailable(slug, noteId);
      setSlugAvailable(available);
    } finally {
      setSlugChecking(false);
    }
  };

  const handleSlideChange = (index, field, value) => {
    setSlides(prev => prev?.map((s, i) => i === index ? { ...s, [field]: value } : s));
  };

  const addSlide = (type) => {
    setSlides(prev => [...prev, emptySlide(type)]);
  };

  const deleteSlide = (index) => {
    setSlides(prev => prev?.filter((_, i) => i !== index));
  };

  const moveSlide = (index, direction) => {
    const newSlides = [...slides];
    const target = index + direction;
    if (target < 0 || target >= newSlides?.length) return;
    [newSlides[index], newSlides[target]] = [newSlides?.[target], newSlides?.[index]];
    setSlides(newSlides);
  };

  const handleSave = async () => {
    if (!form?.title?.trim()) { setError('Title is required'); return; }
    if (!slugAvailable) { setError('Slug is not available'); return; }

    try {
      setSaving(true);
      setError(null);

      let savedNoteId = noteId;
      if (isNew || !savedNoteId) {
        const note = await studyNotesService?.createNote(form, user?.id);
        savedNoteId = note?.id;
        setNoteId(savedNoteId);
        trackStudyNoteCreated();
      } else {
        await studyNotesService?.updateNote(savedNoteId, form);
      }

      await slidesService?.saveSlides(savedNoteId, slides);

      if (isNew) navigate(`/tools/notes/${savedNoteId}/edit`, { replace: true });
    } catch (err) {
      setError(err?.message);
    } finally {
      setSaving(false);
    }
  };

  const getLiveUrl = () => `${window.location?.origin}/live/${form?.slug}`;

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-4xl mx-auto px-4 py-6 pb-24 md:pb-8">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/tools/notes')} className="p-2 hover:bg-muted rounded-lg transition-colors">
              <Icon name="ArrowLeft" size={18} />
            </button>
            <h1 className="text-xl font-heading font-bold text-foreground">
              {isNew ? 'New Study Note' : 'Edit Study Note'}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {noteId && (
              <>
                <button
                  onClick={() => setShowQR(true)}
                  className="flex items-center gap-1.5 px-3 py-2 bg-muted hover:bg-muted/80 rounded-lg text-sm transition-colors"
                >
                  <Icon name="QrCode" size={15} />
                  QR Code
                </button>
                <a
                  href={`/tools/notes/present/${form?.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-2 bg-accent/10 hover:bg-accent/20 rounded-lg text-sm text-accent-foreground transition-colors"
                >
                  <Icon name="Play" size={15} />
                  Present
                </a>
              </>
            )}
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5 px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg text-sm font-medium transition-colors disabled:opacity-60"
            >
              {saving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Icon name="Save" size={15} />}
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Settings Panel */}
          <div className="lg:col-span-1 space-y-4">
            <div className="bg-card border border-border rounded-xl p-4">
              <h2 className="text-sm font-semibold text-foreground mb-4">Note Settings</h2>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Title *</label>
                  <input
                    type="text"
                    value={form?.title}
                    onChange={e => handleFormChange('title', e?.target?.value)}
                    placeholder="Sermon or study title"
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Speaker</label>
                  <input
                    type="text"
                    value={form?.speaker}
                    onChange={e => handleFormChange('speaker', e?.target?.value)}
                    placeholder="Pastor / Teacher name"
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Church / Organization</label>
                  <input
                    type="text"
                    value={form?.churchName}
                    onChange={e => handleFormChange('churchName', e?.target?.value)}
                    placeholder="Church name (optional)"
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Description</label>
                  <textarea
                    value={form?.description}
                    onChange={e => handleFormChange('description', e?.target?.value)}
                    placeholder="Brief description (optional)"
                    rows={2}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Custom Slug</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={form?.slug}
                      onChange={e => handleFormChange('slug', e?.target?.value?.toLowerCase()?.replace(/[^a-z0-9-]/g, ''))}
                      className={`w-full px-3 py-2 bg-background border rounded-lg text-sm focus:outline-none focus:ring-2 pr-8 ${
                        !slugAvailable ? 'border-destructive focus:ring-destructive/30' : 'border-border focus:ring-primary/30'
                      }`}
                    />
                    <div className="absolute right-2 top-1/2 -translate-y-1/2">
                      {slugChecking ? (
                        <div className="w-3 h-3 border border-muted-foreground border-t-transparent rounded-full animate-spin" />
                      ) : slugAvailable ? (
                        <Icon name="Check" size={13} color="var(--color-success)" />
                      ) : (
                        <Icon name="X" size={13} color="var(--color-destructive)" />
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">fortifiedmind.org/live/{form?.slug}</p>
                </div>

                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Default Bible Version</label>
                  <select
                    value={form?.defaultVersion}
                    onChange={e => handleFormChange('defaultVersion', e?.target?.value)}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  >
                    {BIBLE_VERSIONS?.map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>

                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-2 block">Sync Mode</label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { value: 'self-paced', label: 'Self-Paced', icon: 'User' },
                      { value: 'live', label: 'Live Sync', icon: 'Radio' },
                    ]?.map(opt => (
                      <button
                        key={opt?.value}
                        onClick={() => handleFormChange('syncMode', opt?.value)}
                        className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${
                          form?.syncMode === opt?.value
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-background border-border text-muted-foreground hover:bg-muted'
                        }`}
                      >
                        <Icon name={opt?.icon} size={12} />
                        {opt?.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-2 block">Theme</label>
                  <div className="grid grid-cols-3 gap-1.5">
                    {THEMES?.map(t => (
                      <button
                        key={t?.value}
                        onClick={() => handleFormChange('theme', t?.value)}
                        className={`flex flex-col items-center gap-1 px-2 py-2 rounded-lg text-xs border transition-colors ${
                          form?.theme === t?.value
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-background border-border text-muted-foreground hover:bg-muted'
                        }`}
                      >
                        <Icon name={t?.icon} size={13} />
                        {t?.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-2 block">Font Size</label>
                  <div className="grid grid-cols-3 gap-1.5">
                    {FONT_SIZES?.map(f => (
                      <button
                        key={f?.value}
                        onClick={() => handleFormChange('fontSize', f?.value)}
                        className={`px-2 py-2 rounded-lg text-xs border transition-colors ${
                          form?.fontSize === f?.value
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-background border-border text-muted-foreground hover:bg-muted'
                        }`}
                      >
                        {f?.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Slides Panel */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-foreground">Slides ({slides?.length})</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => addSlide('verse')}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-accent/10 hover:bg-accent/20 rounded-lg text-xs font-medium text-accent-foreground transition-colors"
                >
                  <Icon name="BookOpen" size={13} />
                  + Verse
                </button>
                <button
                  onClick={() => addSlide('text')}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-muted hover:bg-muted/80 rounded-lg text-xs font-medium transition-colors"
                >
                  <Icon name="Type" size={13} />
                  + Text
                </button>
              </div>
            </div>

            {slides?.length === 0 ? (
              <div className="text-center py-12 bg-card border border-dashed border-border rounded-xl">
                <Icon name="Layers" size={32} className="mx-auto mb-3 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">No slides yet. Add a verse or text slide above.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {slides?.map((slide, index) => (
                  <SlideCard
                    key={slide?._id || slide?.id || index}
                    slide={slide}
                    index={index}
                    total={slides?.length}
                    onChange={handleSlideChange}
                    onDelete={deleteSlide}
                    onMoveUp={(i) => moveSlide(i, -1)}
                    onMoveDown={(i) => moveSlide(i, 1)}
                    defaultVersion={form?.defaultVersion}
                  />
                ))}
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <button
                onClick={() => addSlide('verse')}
                className="flex-1 flex items-center justify-center gap-2 py-3 border-2 border-dashed border-border hover:border-primary/40 rounded-xl text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                <Icon name="BookOpen" size={16} />
                Add Verse Slide
              </button>
              <button
                onClick={() => addSlide('text')}
                className="flex-1 flex items-center justify-center gap-2 py-3 border-2 border-dashed border-border hover:border-primary/40 rounded-xl text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                <Icon name="Type" size={16} />
                Add Text Slide
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* QR Modal */}
      {showQR && noteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-card rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-foreground">QR Code</h3>
              <button onClick={() => setShowQR(false)} className="p-1 hover:bg-muted rounded-lg">
                <Icon name="X" size={18} />
              </button>
            </div>
            <div className="flex justify-center p-4 bg-white rounded-xl mb-4">
              <QRCodeSVG value={getLiveUrl()} size={200} />
            </div>
            <p className="text-xs text-center text-muted-foreground break-all">{getLiveUrl()}</p>
            <button
              onClick={() => navigator.clipboard?.writeText(getLiveUrl())}
              className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 bg-muted hover:bg-muted/80 rounded-lg text-sm transition-colors"
            >
              <Icon name="Copy" size={14} />
              Copy Link
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudyNoteEditor;
